import { createContext, useContext, useState } from "react";
import { Loader2, CalendarCheck2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ["1-10 recruiters", "11-50 recruiters", "51-200 recruiters", "200+ recruiters"];
const FOCUS = ["IT consultancy / staffing agency", "Tech or product company", "BPO / contact centre", "Campus or volume hiring", "RPO / enterprise HR", "Something else"];
const DemoCtx = createContext({ openDemo: () => {} });
export const useDemo = () => useContext(DemoCtx);
export const DemoButton = ({ children, ...p }) => { const { openDemo } = useDemo(); return <button type="button" onClick={openDemo} {...p}>{children}</button>; };

const inp = "w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-colors";

function Field({ label, children }) {
  return <label className="block"><span className="block text-xs font-medium text-slate-600 mb-1.5">{label}</span>{children}</label>;
}

function DemoForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", company: "", size: SIZES[0], focus: FOCUS[0], notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    const message = `Demo request\nTeam size: ${f.size}\nFocus: ${f.focus}${f.notes.trim() ? `\nNotes: ${f.notes.trim()}` : ""}`;
    try {
      const r = await fetch(`${API}/assistant/ticket`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: f.name, email: f.email, company: f.company, topic: "demo", message }) });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Please check your details and try again.");
      onDone(d.number);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="demo-form">
      <div className="grid sm:grid-cols-2 gap-3.5">
        <Field label="Full name"><input required value={f.name} onChange={set("name")} placeholder="Jane Doe" className={inp} data-testid="demo-input-name" /></Field>
        <Field label="Work email"><input required type="email" value={f.email} onChange={set("email")} placeholder="jane@company.com" className={inp} data-testid="demo-input-email" /></Field>
      </div>
      <Field label="Company"><input required value={f.company} onChange={set("company")} placeholder="Acme IT Solutions" className={inp} data-testid="demo-input-company" /></Field>
      <div className="grid sm:grid-cols-2 gap-3.5">
        <Field label="Team size"><select value={f.size} onChange={set("size")} className={inp} data-testid="demo-select-team-size">{SIZES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Primary focus"><select value={f.focus} onChange={set("focus")} className={inp} data-testid="demo-select-use-case">{FOCUS.map((s) => <option key={s}>{s}</option>)}</select></Field>
      </div>
      <Field label="Anything we should know? (optional)"><textarea rows={3} value={f.notes} onChange={set("notes")} maxLength={1500} placeholder="Roles you hire for, monthly volume, tools you use…" className={inp} data-testid="demo-input-notes" /></Field>
      {err && <div className="text-sm text-rose-600" data-testid="demo-error">{err}</div>}
      <button disabled={busy} className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="demo-btn-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck2 className="w-4 h-4" />} Request my demo
      </button>
    </form>
  );
}

export function DemoProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);
  const openDemo = () => { setDone(null); setOpen(true); };
  return (
    <DemoCtx.Provider value={{ openDemo }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-white text-slate-900 rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="demo-dialog">
          {done ? (
            <div className="py-6 text-center" data-testid="demo-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <div className="mt-4 font-display text-2xl font-semibold">You're on the list</div>
              <p className="mt-2 text-slate-600 text-sm">Request #{done} has reached our team. We'll email you within one business day to schedule a walkthrough.</p>
              <button onClick={() => setOpen(false)} className="mt-6 px-5 py-2.5 rounded-xl border border-slate-200 hover:border-slate-300 text-sm font-medium" data-testid="demo-close">Close</button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Book a personalised demo</DialogTitle>
                <DialogDescription className="text-slate-600">See our AI voice and video interviewers and the coding round screen candidates for your roles — in 30 minutes.</DialogDescription>
              </DialogHeader>
              <DemoForm onDone={setDone} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </DemoCtx.Provider>
  );
}
